import { useEffect, useState } from 'react';

const DEFAULT_RETRY_DELAYS_MS = [2000, 5000, 10000];

/**
 * Refetches on `trigger` change, then keeps retrying (with growing delays)
 * as long as `fetchAndCheck` keeps reporting no data yet — for backends that
 * take a few seconds to reflect an action (e.g. a just-created payment link).
 */
export default function usePollingReload(
  trigger: number | undefined,
  fetchAndCheck: () => Promise<boolean>,
  retryDelaysMs: number[] = DEFAULT_RETRY_DELAYS_MS
) {
  const [isPolling, setIsPolling] = useState(false);

  useEffect(() => {
    if (!trigger) return undefined;
    let cancelled = false;

    const run = async () => {
      const hasData = await fetchAndCheck();
      if (hasData || cancelled) return;

      setIsPolling(true);
      // eslint-disable-next-line no-restricted-syntax
      for (const delay of retryDelaysMs) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => {
          setTimeout(resolve, delay);
        });
        if (cancelled) return;
        // eslint-disable-next-line no-await-in-loop
        const found = await fetchAndCheck();
        if (found || cancelled) break;
      }
      if (!cancelled) setIsPolling(false);
    };

    run();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger]);

  return isPolling;
}
